import { useEffect, useRef, useState } from 'react'

import { useRuleActionNavigation } from '../../shared/hooks/useRuleActionNavigation'
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlags,
} from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { AnimatePresence } from 'framer-motion'

import {
  Box,
  AudioPlayer as DarkAudioPlayer,
  FeedItem as DarkFeedItem,
  DarkProvider,
  Flex,
  Text,
} from '@hivewatch/dark'
import type { StatusPillVariant } from '@hivewatch/dark'

import { AudioPlayer } from '@/components/ui'
import { IncidentQuery } from '@/graphql/generated/operations'
import { IncidentActionType } from '@/graphql/generated/schemas'
import { useAppSelector } from '@/redux/hooks'
import { selectMe } from '@/redux/me/meSlice'
import { mixpanel } from '@/utils/analytics'

import { MixpanelDataIProps } from '../../shared/types/types'
import {
  formatFeedTimestamp,
  getActivityTimeline,
  getTimelineFingerprint,
} from '../../shared/utils/feedUtils'
import { DarkActionIcon } from './DarkIncidentAction'
import { DarkIncidentDetails } from './DarkIncidentDetails'
import { FeedItemsSkeleton } from './FeedItemsSkeleton'
import { ActionHeader, ActionIcon } from './IncidentAction'
import { IncidentNoteMedia } from './IncidentNoteMedia'
import { ResolutionContentItem } from './ResolutionContentItem'
import { ShowMore } from './ShowMore'

const getActionStatusPill = (
  actionType: string
): { statusVariant?: StatusPillVariant; statusLabel?: string } => {
  switch (actionType) {
    case IncidentActionType.AcknowledgementByOperator:
      return { statusVariant: 'in-progress', statusLabel: 'In Progress' }
    case IncidentActionType.ReleaseByOperator:
      return { statusVariant: 'incomplete', statusLabel: 'Active' }
    case IncidentActionType.Resolved:
      return { statusVariant: 'complete', statusLabel: 'Resolved' }
    default:
      return {}
  }
}

interface DarkFeedIProps {
  incident?: IncidentQuery['incident']
  mixpanelData?: MixpanelDataIProps
  isLoading?: boolean
  onClose?: () => void
}

export const DarkFeed = ({
  incident,
  mixpanelData,
  isLoading = false,
  onClose,
}: DarkFeedIProps) => {
  const { t } = useTranslation()
  const isTraceLogsEnabled = useFeatureFlag(FeatureFlag.traceLogs)
  const colorScheme = useLightMode()
  const [items, setItems] = useState(null)
  const me = useAppSelector(selectMe)
  const {
    attachMediaToIncidentNote: isAttachMediaToIncidentNoteEnabled,
    salesDatabaseReset: isSalesEnvironment,
    incidentDrawerRebrand: isIncidentDrawerRebrandEnabled,
  } = useFeatureFlags()

  const handleRuleActionClick = useRuleActionNavigation({
    mixpanelData,
    onClose,
    items,
  })

  const prevFingerprintRef = useRef<string | null>(null)

  useEffect(() => {
    const fingerprint = getTimelineFingerprint(incident)

    if (fingerprint && fingerprint === prevFingerprintRef.current) {
      return
    }
    prevFingerprintRef.current = fingerprint

    const actions = incident?.actions
    const events = incident?.events
    const audioMessages = incident?.audioMessages?.edges?.map((a) => a?.node)
    const notes = incident?.notes.edges?.map((a) => a?.node)

    if (actions && audioMessages && notes && events) {
      const activityTimeLine = getActivityTimeline(
        actions,
        events,
        audioMessages,
        notes,
        me,
        isSalesEnvironment
      )
      setItems(activityTimeLine)
    }
  }, [incident, me, isSalesEnvironment])

  const trackExpandDescriptionClick = (eventType: 'more' | 'less'): void => {
    let eventName: string
    switch (eventType) {
      case 'more':
        eventName = 'Show More Button Pressed (Feed)'
        break
      case 'less':
        eventName = 'Show Less Button Pressed (Feed)'
        break
    }
    mixpanel.track(eventName, mixpanelData)
  }

  return (
    <DarkProvider>
      <Box
        borderTop='1px solid'
        borderTopColor={
          colorScheme === 'dark' ? 'border.subtle' : 'light.border.subtle'
        }
        p='24px'
      >
        {isLoading || !items ? (
          <FeedItemsSkeleton />
        ) : (
          <AnimatePresence>
            <DarkIncidentDetails
              incident={incident}
              mixpanelData={mixpanelData}
            />

            {items?.length > 0 && (
              <Text
                color={
                  colorScheme === 'dark'
                    ? 'text.secondary'
                    : 'light.text.primary'
                }
                fontFamily="'Soehne Kraftig', sans-serif"
                fontSize='16px'
                fontWeight={600}
                lineHeight='normal'
                mb='10px'
                mt='24px'
                pt='24px'
              >
                {t('incidents:incident_drawer_timeline_feed')}
              </Text>
            )}

            <Flex direction='column' gap='4px'>
              {items.map((a, i: number) => {
                const actionType = a?.type || a?.nonActionType
                const { statusVariant, statusLabel } =
                  getActionStatusPill(actionType)
                return (
                  <DarkFeedItem
                    colorScheme={colorScheme}
                    icon={
                      isIncidentDrawerRebrandEnabled
                        ? DarkActionIcon(actionType, colorScheme, {
                            priority: a?.history?.newValue,
                          })
                        : ActionIcon(actionType)
                    }
                    key={a?.id ?? i}
                    message={ActionHeader({
                      actionType,
                      username: `${a?.creator?.firstName} ${a?.creator?.lastName}`,
                      roleName: a?.creator?.roleName,
                      systemMessage: a?.systemMessage,
                      technicianName: `${a?.deviceRepairOrder?.technician?.firstName} ${a?.deviceRepairOrder?.technician?.lastName}`,
                      history: a?.history,
                      isRuleAction: a?.isRuleAction,
                      emailRecipientCount: a?.emailRecipients?.length,
                      onRuleClick: a?.ruleId
                        ? () => handleRuleActionClick(a.ruleId)
                        : undefined,
                      shouldShowStatusTag: false,
                    })}
                    statusLabel={statusLabel}
                    statusVariant={statusVariant}
                    timestamp={formatFeedTimestamp(a.createdAt)}
                  >
                    {a?.audioClip?.url &&
                      (isIncidentDrawerRebrandEnabled ? (
                        <DarkAudioPlayer
                          colorScheme={colorScheme}
                          url={a.audioClip.url}
                        />
                      ) : (
                        <AudioPlayer url={a.audioClip.url} />
                      ))}
                    {/* Resolution actions use resolutionContent.segments (supports TEXT and EVENT_REF types).
                        Notes use message field and render via ShowMore for plain text display. */}
                    {a?.resolutionContent && isTraceLogsEnabled ? (
                      <ResolutionContentItem
                        content={a.resolutionContent}
                        handleShowLess={() =>
                          trackExpandDescriptionClick('less')
                        }
                        handleShowMore={() =>
                          trackExpandDescriptionClick('more')
                        }
                      />
                    ) : a?.message ? (
                      <ShowMore
                        handleShowLess={() =>
                          trackExpandDescriptionClick('less')
                        }
                        handleShowMore={() =>
                          trackExpandDescriptionClick('more')
                        }
                        message={a.message}
                      />
                    ) : null}
                    {isAttachMediaToIncidentNoteEnabled &&
                      a?.media?.length > 0 && (
                        <IncidentNoteMedia files={a?.media} />
                      )}
                  </DarkFeedItem>
                )
              })}
            </Flex>
          </AnimatePresence>
        )}
      </Box>
    </DarkProvider>
  )
}
